import React, { useMemo, ReactNode } from "react";
import { Component, ComponentType } from "@/types";
import type { FormSubmissionResult } from "@/types/form.types";
import { InteractionContext, determineCapabilities } from "./InteractionContextRuntime";

// Form submit configuration
export interface FormSubmitConfig {
  formContainerId: string;
  submitData?: Record<string, unknown>;
}

// Bounds interface for resize operations
export interface Bounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

// Interaction modes for the unified rendering system
export type InteractionMode = "editing" | "viewing" | "functional" | "pin-placement";

// Capabilities available in each mode
export interface InteractionCapabilities {
  canSelect: boolean;
  canResize: boolean;
  canDrag: boolean;
  canInteract: boolean;
  canNavigate: boolean;
  canEditProperties: boolean;
  canDelete: boolean;
  canPlacePin?: boolean;
}

// User permissions that affect interactions
export interface UserPermissions {
  canEdit: boolean;
  canView: boolean;
  canInteract: boolean;
  isOwner: boolean;
  customPermissions?: Record<string, boolean>;
}

// Device binding configuration for connecting UI components to physical devices
export interface DeviceBinding {
  type: 'kasa';                    // Extensible for future device types
  deviceId: string;                // Device's unique ID (from workflow_devices)
  deviceName: string;              // Display name for the device
  controllerDeviceId: string;      // Pi/ESP32 that controls this device
  orgSlug: string;                 // Organization that owns this device
  capabilities: {
    toggle: boolean;               // Supports on/off
    brightness: boolean;           // Supports dimming (0-100)
  };
}

// Sensor control configuration
export interface SensorControlConfig {
  sensorId: string;
  action: "read" | "write" | "control";
  parameters: Record<string, unknown>;
  timeout?: number;
}

// Sensor response structure
export interface SensorResponse {
  success: boolean;
  data?: unknown;
  error?: string;
  timestamp: number;
}

// Collaboration configuration
export interface CollaborationConfig {
  action: "todo-create" | "comment-add" | "approval-request" | "data-update";
  target: string;
  data: unknown;
  users?: string[];
}

// Collaboration response structure
export interface CollaborationResponse {
  success: boolean;
  id?: string;
  data?: unknown;
  error?: string;
}

// Navigation configuration
export interface NavigationConfig {
  targetPageId?: string;
  transition?: "fade" | "slide" | "instant";
  conditions?: Array<{
    field: string;
    operator: "equals" | "contains" | "greater" | "less";
    value: unknown;
  }>;
  parameters?: Record<string, unknown>;

  // Cross-project navigation (Phase 3)
  targetProjectId?: string;
  targetOrganizationId?: string;
  openInNewTab?: boolean;
}

// Content swap configuration (Phase 4)
export interface ContentSwapConfig {
  containerId: string;
  targetContentId: string;
  transition?: "fade" | "slide" | "instant";
}

// Interaction handlers for components
export interface InteractionHandlers {
  // Editor mode handlers
  onSelect?: (componentId: string) => void;
  onResize?: (componentId: string, bounds: Bounds) => void;
  onDelete?: (componentId: string) => void;
  onEditProperties?: (componentId: string) => void;

  // Functional mode handlers
  onNavigate?: (config: NavigationConfig) => Promise<boolean>;
  onNavigateProject?: (config: NavigationConfig) => Promise<boolean>;
  onApiCall?: (endpoint: string, data: unknown) => Promise<unknown>;
  onSensorControl?: (config: SensorControlConfig) => Promise<SensorResponse>;
  onCollaboration?: (config: CollaborationConfig) => Promise<CollaborationResponse>;
  onFormSubmit?: (config: FormSubmitConfig) => Promise<FormSubmissionResult>;
  onToggleVisibility?: (targetIds: string[], showOnlyThisId?: string) => void;
  onSwapContent?: (config: ContentSwapConfig) => void;
  onAddTableRow?: (tableId: string) => void;
  onEditTableRow?: (tableId: string, rowId: string, rowData: Record<string, unknown>) => void;
  onDeleteTableRow?: (tableId: string, rowId: string, rowData: Record<string, unknown>) => void;
  onAddChecklistItem?: (checklistId: string) => void;
  onAddGanttTask?: (ganttId: string) => void;
  onEditGanttTask?: (ganttId: string, taskId: string, taskData: Record<string, unknown>) => void;
  onAddKanbanItem?: (kanbanId: string) => void;
  onEditKanbanItem?: (kanbanId: string, itemId: string, itemData: Record<string, unknown>) => void;

  // File storage handler
  onOpenFileManager?: (componentId: string, folder?: string) => void;
}

// Complete interaction context
export interface InteractionContextValue {
  mode: InteractionMode;
  capabilities: InteractionCapabilities;
  handlers: InteractionHandlers;
  permissions: UserPermissions;
  // Helper methods
  isEditMode: () => boolean;
  isViewMode: () => boolean;
  isFunctionalMode: () => boolean;
  canInteractWith: (component: Component) => boolean;
}

// Provider props
export interface InteractionContextProviderProps {
  mode: InteractionMode;
  userPermissions: UserPermissions;
  handlers?: InteractionHandlers;
  children: ReactNode;
}

// Provider component
export const InteractionContextProvider: React.FC<InteractionContextProviderProps> = ({
  mode,
  userPermissions,
  handlers = {},
  children,
}) => {
  const context = useMemo<InteractionContextValue>(() => {
    const capabilities = determineCapabilities(mode, userPermissions);

    return {
      mode,
      capabilities,
      handlers,
      permissions: userPermissions,

      // Helper methods
      isEditMode: () => mode === "editing",
      isViewMode: () => mode === "viewing",
      isFunctionalMode: () => mode === "functional",

      // Check if a specific component can be interacted with
      canInteractWith: (component: Component) => {
        if (mode === "editing") {
          return capabilities.canSelect;
        }

        if (mode === "functional") {
          // Check component type for functional interactions
          const interactiveTypes = [
            ComponentType.BUTTON,
            ComponentType.INPUT,
            ComponentType.ICON,
            ComponentType.CHART,
            ComponentType.BAR_CHART,
            ComponentType.LINE_CHART,
            ComponentType.PIE_CHART,
            ComponentType.AREA_CHART,
            ComponentType.SCATTER_CHART,
            ComponentType.HEATMAP_CHART,
            ComponentType.TREEMAP_CHART,
            ComponentType.MAP_CHART,
            ComponentType.TABLE,
            ComponentType.ADVANCED_TABLE,
            ComponentType.FORM_CONTAINER,
            ComponentType.FILE_STORAGE,
            ComponentType.KANBAN_BOARD,
            ComponentType.GANTT_CHART,
            ComponentType.CALENDAR,
            ComponentType.RADIAL_DIAL,
            ComponentType.TOGGLE_SWITCH,
            ComponentType.PUSH_BUTTON,
            ComponentType.ICON_BUTTON,
            ComponentType.STEPPER,
            ComponentType.SLIDER,
            ComponentType.SEGMENTED_CONTROL,
            ComponentType.COLOR_PICKER,
            ComponentType.THERMOSTAT,
            ComponentType.MODAL,
            ComponentType.CHECKLIST,
            ComponentType.HORIZONTAL_TIMELINE,
            ComponentType.ASSIGNMENT_WIDGET,
            ComponentType.QUESTIONNAIRE,
            ComponentType.VOTING_CARD,
          ];
          return interactiveTypes.includes(component.type) && capabilities.canInteract;
        }

        return false;
      },
    };
  }, [mode, userPermissions, handlers]);

  return (
    <InteractionContext.Provider value={context}>
      {children}
    </InteractionContext.Provider>
  );
};
